import { Gauge } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { IStatItem } from "./types";
import { SummaryCard } from "./SummaryCard";
import { formatSummaryNumber, summaryNumber } from "./summaryData";
import { ConversionModeSelector } from "./ChartModeSelector";
import { useDistributionMode } from "./useDistributionMode";

export const DonutCard = ({ item, preferenceKey }: { item: IStatItem; preferenceKey: string }) => {
	const { t, i18n } = useTranslation();
	const [mode, setMode] = useDistributionMode(preferenceKey, "linear");
	const value = summaryNumber(item.data);
	const percent = value === null ? null : Math.min(100, Math.max(0, value));
	const display = value === null ? "—" : `${formatSummaryNumber(value, i18n.language)}%`;
	const parts = value === null ? [] : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 20 }).formatToParts(value > 50 ? Math.trunc(value) : value);
	const percentage = value === null ? "—" : <span className="conversion-value">
		<span>{parts.filter(part => part.type !== "decimal" && part.type !== "fraction").map(part => part.value).join("")}</span>
		<small>{parts.filter(part => part.type === "decimal" || part.type === "fraction").map(part => part.value).join("")}%</small>
	</span>;
	return <SummaryCard title={item.title} icon={<Gauge size={20} aria-hidden="true" />} actions={<ConversionModeSelector value={mode} onChange={setMode} />}>
		<div className={`conversion${mode === "donut" ? " conversion-circular" : ""}`}>
			{mode === "donut" ? <>
				<div className="conversion-ring" role="img" aria-label={`${item.title}: ${display}`} title={display}>
					<svg viewBox="0 0 160 160" aria-hidden="true">
						<circle cx="80" cy="80" r="68" fill="none" stroke="#eef1f5" strokeWidth="14" />
						<circle className="conversion-progress" cx="80" cy="80" r="68" fill="none" stroke="#167ca6" strokeWidth="14" pathLength="100" strokeDasharray={`${percent ?? 0} 100`} transform="rotate(-90 80 80)" />
					</svg>
					<div className="metric" aria-hidden="true">{percentage}</div>
				</div>
				<p className="caption">{item.subtitle}</p>
			</> : <>
				<div><div className="metric" title={display} aria-label={display}><span aria-hidden="true">{percentage}</span></div><p className="caption">{item.subtitle}</p></div>
				<div>
					<div className="track" role="img" aria-label={`${item.title}: ${display}`}>
						<span className="segment" style={{ width: `${percent ?? 0}%`, background: "#167ca6" }} />
					</div>
					<div className="scale" aria-hidden="true"><span>0%</span><span>100%</span></div>
				</div>
			</>}
			{value === null && <p className="caption" role="status">{t("common.noData")}</p>}
		</div>
	</SummaryCard>;
};
